"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { CalendarGrid } from "@/components/calendar/CalendarGrid";
import { compactAmd } from "@/lib/config";

type Props = {
  locale: string;
  /** YYYY-MM-DD unavailable nights (confirmed bookings + host-closed days). */
  unavailable: string[];
  /** Per-day price overrides ({ "YYYY-MM-DD": price }). */
  prices: Record<string, number>;
  /** Stay's default nightly price. */
  defaultPrice: number;
  checkIn: string;
  checkOut: string;
  onChange: (checkIn: string, checkOut: string) => void;
};

/** Nights between two YYYY-MM-DD keys: check-in inclusive, check-out exclusive. */
function nightsBetween(checkIn: string, checkOut: string): string[] {
  const out: string[] = [];
  const d = new Date(`${checkIn}T00:00:00.000Z`);
  const end = new Date(`${checkOut}T00:00:00.000Z`);
  while (d < end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

/**
 * A read-only availability calendar that doubles as a range picker. Clicking
 * picks check-in, then check-out; it stays in sync with the date fields via
 * the controlled checkIn/checkOut props.
 */
export function BookingCalendar({
  locale,
  unavailable,
  prices,
  defaultPrice,
  checkIn,
  checkOut,
  onChange,
}: Props) {
  const t = useTranslations("Booking");
  const unavailableSet = useMemo(() => new Set(unavailable), [unavailable]);
  const selectingCheckout = Boolean(checkIn) && !checkOut;

  function handleSelect(key: string) {
    // Completing a range: a later day with all in-between nights free.
    if (selectingCheckout && key > checkIn) {
      const range = nightsBetween(checkIn, key);
      if (!range.some((d) => unavailableSet.has(d))) {
        onChange(checkIn, key);
        return;
      }
      // Spans a busy night — fall through and restart from this day.
    }
    // Start a new range, but never on an unavailable day.
    if (!unavailableSet.has(key)) onChange(key, "");
  }

  function dayClass(key: string, isPast: boolean) {
    if (isPast) return "border-transparent bg-transparent text-stone/40";
    const isEndpoint = key === checkIn || key === checkOut;
    if (isEndpoint)
      return "border-forest-500 bg-forest-500 text-white ring-2 ring-forest-300";
    // Busy days always read as red, even inside a (typed) invalid range.
    if (unavailableSet.has(key))
      return "border-red-200 bg-red-100 text-red-700";
    const inRange = checkIn && checkOut && key > checkIn && key < checkOut;
    if (inRange) return "border-forest-200 bg-forest-100 text-forest-700";
    return "border-sand bg-cream text-bark hover:border-forest-300";
  }

  function renderSub(key: string, isPast: boolean) {
    if (isPast) return null;
    const override = prices[key];
    return (
      <span className={override != null ? "font-bold" : "opacity-60"}>
        {compactAmd(override ?? defaultPrice)}
      </span>
    );
  }

  function isDisabled(key: string) {
    // While choosing a check-out, allow clicking later days (validated on click)
    // so back-to-back stays work; otherwise busy days can't start a stay.
    if (selectingCheckout) return false;
    return unavailableSet.has(key);
  }

  return (
    <div className="rounded-2xl border border-sand bg-cream p-4 sm:p-5">
      <CalendarGrid
        locale={locale}
        focus={checkIn || undefined}
        dayClass={dayClass}
        isDisabled={isDisabled}
        renderSub={renderSub}
        onSelect={handleSelect}
      />
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone">
        <span className="flex items-center gap-1.5">
          <span className="h-3.5 w-3.5 rounded border border-sand bg-cream" />
          {t("calendarAvailable")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3.5 w-3.5 rounded border border-red-200 bg-red-100" />
          {t("calendarBusy")}
        </span>
        <span className="ml-auto text-stone">{t("calendarHint")}</span>
      </div>
    </div>
  );
}
